import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, Check, X, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "wango_cookie_consent_v1";

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (!saved) {
        const timer = setTimeout(() => {
          setVisible(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      // Ignore private browsing restrictions
    }
  }, []);

  useEffect(() => {
    const handleReopen = () => {
      const saved = localStorage.getItem(CONSENT_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setAnalytics(!!parsed.analytics);
          setMarketing(!!parsed.marketing);
        } catch {
          // ignore
        }
      }
      setShowSettings(true);
      setVisible(true);
    };

    window.addEventListener("open-cookie-settings", handleReopen);
    return () => window.removeEventListener("open-cookie-settings", handleReopen);
  }, []);

  const saveConsent = (analyticsVal, marketingVal) => {
    const consent = {
      essential: true,
      analytics: analyticsVal,
      marketing: marketingVal,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    } catch {
      // ignore
    }
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6 pointer-events-none">
      <div className="mx-auto max-w-4xl pointer-events-auto rounded-2xl border border-white/15 bg-[#121212] shadow-2xl p-6 sm:p-7 text-white transition-all duration-300">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#C5A059]/15 text-[#C5A059] border border-[#C5A059]/30">
              <Cookie className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                Privatsphäre & Cookie-Einstellungen
              </h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                Wir nutzen Cookies, um unsere Website für Sie optimal zu gestalten, Funktionen bereitzustellen und Zugriffe zu analysieren. Sie können selbst entscheiden, welche Kategorien Sie zulassen möchten. Weitere Infos finden Sie in unserer{" "}
                <Link to="/datenschutz" className="text-[#C5A059] underline hover:text-white transition-colors">
                  Datenschutzerklärung
                </Link>.
              </p>
            </div>
          </div>

          {!showSettings && (
            <div className="flex flex-wrap items-center gap-3 shrink-0 lg:flex-nowrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                data-testid="cookie-settings-btn"
                className="rounded-full border-white/20 bg-white/5 text-xs text-white hover:bg-white/10 hover:border-white/40"
              >
                <Settings2 className="mr-1.5 h-3.5 w-3.5" />
                Einstellungen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveConsent(false, false)}
                data-testid="cookie-essential-btn"
                className="rounded-full border-white/20 bg-white/5 text-xs text-white hover:bg-white/10 hover:border-[#C5A059]"
              >
                Nur essenzielle
              </Button>
              <Button
                size="sm"
                onClick={() => saveConsent(true, true)}
                data-testid="cookie-accept-all-btn"
                className="rounded-full bg-[#C5A059] text-xs font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] px-5"
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Alle akzeptieren
              </Button>
            </div>
          )}
        </div>

        {showSettings && (
          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-[#C5A059]" />
                    <span className="font-semibold text-sm text-white">Essenziell</span>
                  </div>
                  <span className="text-[10px] font-bold text-[#C5A059] uppercase tracking-wider bg-[#C5A059]/10 px-2 py-0.5 rounded">
                    Immer aktiv
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Notwendig für die grundlegende Funktion und Sicherheit der Website.
                </p>
              </div>

              <div
                onClick={() => setAnalytics(!analytics)}
                className={`cursor-pointer rounded-xl border p-4 transition-all ${
                  analytics
                    ? "border-[#C5A059] bg-[#C5A059]/10"
                    : "border-white/10 bg-white/5 hover:border-white/20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-white">Statistiken</span>
                  <div
                    className={`h-5 w-5 rounded flex items-center justify-center border transition-all ${
                      analytics
                        ? "bg-[#C5A059] border-[#C5A059] text-[#0A0A0A]"
                        : "border-white/30 bg-transparent"
                    }`}
                  >
                    {analytics && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Hilft uns, die Website durch anonyme Besucherstatistiken zu verbessern.
                </p>
              </div>

              <div
                onClick={() => setMarketing(!marketing)}
                className={`cursor-pointer rounded-xl border p-4 transition-all ${
                  marketing
                    ? "border-[#C5A059] bg-[#C5A059]/10"
                    : "border-white/10 bg-white/5 hover:border-white/20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-white">Marketing</span>
                  <div
                    className={`h-5 w-5 rounded flex items-center justify-center border transition-all ${
                      marketing
                        ? "bg-[#C5A059] border-[#C5A059] text-[#0A0A0A]"
                        : "border-white/30 bg-transparent"
                    }`}
                  >
                    {marketing && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Ermöglicht personalisierte Inhalte und Relevanz-Optimierung.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(false)}
                className="text-xs text-muted-foreground hover:text-white"
              >
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={() => saveConsent(analytics, marketing)}
                className="rounded-full bg-[#C5A059] text-xs font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] px-6"
              >
                Auswahl speichern
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
